import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/playlists.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ConfirmPopup from '../../components/common/confirm-popup.component';
import PlaylistSettings from '../../components/content/playlist-settings.component';
import PlaylistVideos from '../../components/content/playlist-videos.component';
import PlaylistPosters from '../../components/content/playlist-posters.component';

const ContentPlaylistAddEditView = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabsConfig, setTabsConfig] = useState([
    {
      value: 'playlistSettings',
      label: 'Playlist Settings',
      component: PlaylistSettings,
    },
  ]);
  const [tabValue, setTabValue] = useState(tabsConfig[0].value);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [playlistDetail, setPlaylistDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [hasFormValueChanged, setHasFormValueChanged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const tabValueRef = useRef('');

  const formValueChangedCallback = (val) => {
    setHasFormValueChanged(val);
  };

  const handleRefreshData = () => {
    setReloadCounter(reloadCounter + 1);
  };

  const handleTabSwitchSuccess = () => {
    setTabValue(tabValueRef.current);
    setHasFormValueChanged(false);
    setShowWarning(false);
  };

  useEffect(() => {
    if (!editMode) {
      setLoading(false);
      return;
    }

    viewDataById(id)
      .then((res) => {
        const baseTabs = [
          {
            value: 'playlistSettings',
            label: 'Playlist Settings',
            component: PlaylistSettings,
          },
        ];

        if (res.data.level === 1) {
          baseTabs.push(
            {
              value: 'addVideos',
              label: 'Add Videos',
              component: PlaylistVideos,
            },
            {
              value: 'addPosters',
              label: 'Add Posters',
              component: PlaylistPosters,
            }
          );
          setTabsConfig(baseTabs);
        }
        setPlaylistDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id, reloadCounter]);

  return editMode && (Number.isNaN(Number(id)) || showPageNotFound) ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={
        editMode
          ? `Edit Playlist Details - ${playlistDetail.name}`
          : 'Create New Playlist'
      }
      showBack
      handleBack={() => navigate(ROUTE_PATH.CONTENT_ALL_PLAYLISTS)}
    >
      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => {
            if (hasFormValueChanged) {
              tabValueRef.current = v;
              setShowWarning(true);
            } else {
              setTabValue(v);
            }
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading && (
            <>
              {tabsConfig.map(({ value, component: Component }) =>
                tabValue === value ? (
                  <Component
                    key={value}
                    playlistDetail={playlistDetail}
                    handleRefreshData={handleRefreshData}
                    formValueChangedCallback={formValueChangedCallback}
                    editMode={editMode}
                  />
                ) : null
              )}
            </>
          )}
        </Box>
      </Box>

      {showWarning && (
        <ConfirmPopup
          title="Tab Switch Warning"
          message="Do you want to switch tab without saving data? Before switching tab, save your changes otherwise you lost all your changes and can not undo this action!"
          onCancel={() => setShowWarning(false)}
          onSuccess={() => handleTabSwitchSuccess()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

ContentPlaylistAddEditView.propTypes = {
  editMode: PropTypes.bool.isRequired,
};

export default ContentPlaylistAddEditView;
